import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import { getMenuDishes, getMenuHeroImage, getMenuCollageImages } from "./foodServiceData";

/* ═══════════════════════════════════════════════════════════════════════════
   TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */
const T = {
  navy: "#0f172a",
  gold: "#d4a373",
  goldLight: "#f8f5f0",
  cream: "#f8f5f0",
  white: "#ffffff",
  bg: "#f8f5f0",
  slate: "#6b7280",
  slateLt: "#9ca3af",
  line: "#e5e7eb",
};

const CATEGORY_COLORS: Record<string, string> = {
  "Veg": "#6b7280",
  "Non-Veg": "#9b2c2c",
  "Desserts": "#8d6e00",
  "Appetizer": "#6b7280",
  "Beverage": "#6b7280",
};

const SLIDE_MS = 350;
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

/* ═══════════════════════════════════════════════════════════════════════════
   MENU PREVIEW CARD (shown inline below the dropdown)
   ═══════════════════════════════════════════════════════════════════════════ */
interface MenuPreviewProps {
  menuName: string;
  priceLabel: string;
  onExplore: () => void;
}

export const MenuPreviewCard: React.FC<MenuPreviewProps> = ({ menuName, priceLabel, onExplore }) => {
  const collageImages = getMenuCollageImages(menuName);
  const dishes = getMenuDishes(menuName);

  return (
    <Box
      sx={{
        mt: 2,
        borderRadius: "14px",
        overflow: "hidden",
        bgcolor: T.white,
        border: `1px solid ${T.line}`,
        boxShadow: "0 2px 16px rgba(0,0,0,0.05)",
        animation: "menuCardSlideUp 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
        "@keyframes menuCardSlideUp": {
          "0%": { opacity: 0, transform: "translateY(16px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
        "&:hover": {
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
        },
        transition: "box-shadow 0.3s ease",
      }}
    >
      {/* Collage thumbnail row */}
      <Box sx={{ display: "flex", height: 100, overflow: "hidden" }}>
        {collageImages.slice(0, 4).map((img, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              backgroundImage: `url(${img})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRight: i < 3 ? `2px solid ${T.white}` : "none",
              transition: "transform 0.4s ease",
              "&:hover": { transform: "scale(1.06)" },
            }}
          />
        ))}
      </Box>

      {/* Info row */}
      <Box sx={{ px: 2, py: 1.8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: T.navy, lineHeight: 1.2 }}>
            {menuName}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: T.slateLt, mt: 0.3 }}>
            {priceLabel} &middot; {dishes.length > 0 ? `${dishes.length} dishes` : "Full menu"}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<MenuBookOutlinedIcon sx={{ fontSize: 15 }} />}
          onClick={onExplore}
          sx={{
            textTransform: "none",
            fontSize: 12,
            fontWeight: 600,
            borderRadius: "10px",
            bgcolor: T.gold,
            color: T.white,
            px: 2,
            py: 0.7,
            boxShadow: "0 2px 8px rgba(201,169,110,0.3)",
            "&:hover": {
              bgcolor: "#b8963f",
              boxShadow: "0 4px 16px rgba(201,169,110,0.4)",
            },
          }}
        >
          Explore Menu
        </Button>
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MENU PLACEHOLDER
   ═══════════════════════════════════════════════════════════════════════════ */
export const MenuPlaceholder: React.FC = () => (
  <Box
    sx={{
      mt: 2,
      borderRadius: "14px",
      border: "1.5px dashed #ddd5c8",
      bgcolor: "#fffaf3",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 3.5,
      px: 2,
      animation: "menuCardSlideUp 0.3s ease",
      "@keyframes menuCardSlideUp": {
        "0%": { opacity: 0, transform: "translateY(10px)" },
        "100%": { opacity: 1, transform: "translateY(0)" },
      },
    }}
  >
    <MenuBookOutlinedIcon sx={{ fontSize: 30, color: T.gold, opacity: 0.5, mb: 1 }} />
    <Typography sx={{ fontSize: 13, color: T.slateLt, fontWeight: 500 }}>
      Select a menu to explore dishes
    </Typography>
  </Box>
);

/* ═══════════════════════════════════════════════════════════════════════════
   MENU GALLERY MODAL — Full dish gallery with tabs + premium lightbox
   ═══════════════════════════════════════════════════════════════════════════ */
interface ModalProps {
  open: boolean;
  onClose: () => void;
  menuName: string;
  priceLabel: string;
}

const MenuGalleryModal: React.FC<ModalProps> = ({ open, onClose, menuName, priceLabel }) => {
  const dishes = getMenuDishes(menuName);
  const heroImage = getMenuHeroImage(menuName);
  const [activeTab, setActiveTab] = useState("All");
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  /* Transition state for premium crossfade */
  const [prevLbIdx, setPrevLbIdx] = useState<number | null>(null);
  const [slideDir, setSlideDir] = useState<1 | -1>(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const animTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Lightbox open/close animation */
  const [lbVisible, setLbVisible] = useState(false);
  const [lbClosing, setLbClosing] = useState(false);

  /* Touch / swipe */
  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);
  const [dragOffset, setDragOffset] = useState(0);
  const isDragging = useRef(false);

  /* Edge bounce */
  const [bounceDir, setBounceDir] = useState<"left" | "right" | null>(null);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(dishes.map((d) => d.category)));
    return ["All", ...cats];
  }, [dishes]);

  const filteredDishes = useMemo(
    () => (activeTab === "All" ? dishes : dishes.filter((d) => d.category === activeTab)),
    [dishes, activeTab],
  );

  /* Open lightbox with animation */
  const openLightbox = (i: number) => {
    setLightboxIdx(i);
    setLbClosing(false);
    requestAnimationFrame(() => setLbVisible(true));
  };

  /* Close lightbox with reverse animation */
  const closeLightbox = () => {
    setLbClosing(true);
    setTimeout(() => {
      setLightboxIdx(null);
      setLbVisible(false);
      setLbClosing(false);
      setPrevLbIdx(null);
      setIsAnimating(false);
    }, 280);
  };

  /* Direction-aware slide navigation */
  const slideToLb = useCallback((newIdx: number, dir: 1 | -1) => {
    if (isAnimating || filteredDishes.length <= 1 || lightboxIdx === null) return;
    if (newIdx === lightboxIdx) return;

    /* Preload target image */
    const img = new Image();
    img.src = filteredDishes[newIdx].image.replace("w=400&h=400", "w=800&h=800");

    setPrevLbIdx(lightboxIdx);
    setSlideDir(dir);
    setLightboxIdx(newIdx);
    setIsAnimating(true);

    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    animTimerRef.current = setTimeout(() => {
      setIsAnimating(false);
      setPrevLbIdx(null);
    }, SLIDE_MS + 20);
  }, [isAnimating, filteredDishes, lightboxIdx]);

  const nextLightbox = useCallback(() => {
    if (lightboxIdx === null) return;
    const next = (lightboxIdx + 1) % filteredDishes.length;
    slideToLb(next, 1);
    if (lightboxIdx === filteredDishes.length - 1) {
      setBounceDir("right");
      setTimeout(() => setBounceDir(null), 300);
    }
  }, [lightboxIdx, filteredDishes.length, slideToLb]);

  const prevLightbox = useCallback(() => {
    if (lightboxIdx === null) return;
    const prev = (lightboxIdx - 1 + filteredDishes.length) % filteredDishes.length;
    slideToLb(prev, -1);
    if (lightboxIdx === 0) {
      setBounceDir("left");
      setTimeout(() => setBounceDir(null), 300);
    }
  }, [lightboxIdx, filteredDishes.length, slideToLb]);

  /* Keyboard navigation */
  useEffect(() => {
    if (lightboxIdx === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") nextLightbox();
      else if (e.key === "ArrowLeft") prevLightbox();
      else if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxIdx, nextLightbox, prevLightbox]);

  /* Preload adjacent images */
  useEffect(() => {
    if (lightboxIdx === null || filteredDishes.length <= 1) return;
    const preload = (idx: number) => {
      const img = new Image();
      img.src = filteredDishes[idx].image.replace("w=400&h=400", "w=800&h=800");
    };
    preload((lightboxIdx + 1) % filteredDishes.length);
    if (filteredDishes.length > 2) preload((lightboxIdx - 1 + filteredDishes.length) % filteredDishes.length);
  }, [lightboxIdx, filteredDishes]);

  /* Touch handlers */
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
    isDragging.current = true;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
    setDragOffset(touchDeltaX.current);
  };
  const onTouchEnd = () => {
    isDragging.current = false;
    if (touchDeltaX.current < -60) nextLightbox();
    else if (touchDeltaX.current > 60) prevLightbox();
    setDragOffset(0);
    touchDeltaX.current = 0;
  };

  /* Animation keyframe names */
  const exitAnimName = `mgm-exit-${slideDir > 0 ? "l" : "r"}`;
  const enterAnimName = `mgm-enter-${slideDir > 0 ? "l" : "r"}`;
  const exitX = slideDir === 1 ? "-45%" : "45%";
  const enterX = slideDir === 1 ? "45%" : "-45%";

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            bgcolor: T.cream,
            maxHeight: "90vh",
            animation: `mgm-modalIn 350ms ${EASE}`,
            "@keyframes mgm-modalIn": {
              "0%": { transform: "scale(0.92)", opacity: 0 },
              "100%": { transform: "scale(1)", opacity: 1 },
            },
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: "rgba(13,27,42,0.6)",
              backdropFilter: "blur(8px)",
              animation: `mgm-backdropIn 350ms ease`,
              "@keyframes mgm-backdropIn": {
                "0%": { opacity: 0 },
                "100%": { opacity: 1 },
              },
            },
          },
        }}
      >
        {/* Hero header */}
        <Box
          sx={{
            position: "relative",
            height: { xs: 140, sm: 180 },
            backgroundImage: `url(${heroImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(to top, rgba(13,27,42,0.85) 0%, rgba(13,27,42,0.3) 60%, transparent 100%)",
            }}
          />
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              bgcolor: "rgba(255,255,255,0.9)",
              backdropFilter: "blur(8px)",
              width: 34,
              height: 34,
              "&:hover": { bgcolor: T.white },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18, color: T.navy }} />
          </IconButton>

          <Box sx={{ position: "absolute", bottom: 16, left: 20, right: 20 }}>
            <Typography sx={{ fontSize: { xs: 20, sm: 26 }, fontWeight: 800, color: T.white, lineHeight: 1.15, textShadow: "0 1px 6px rgba(0,0,0,0.3)" }}>
              {menuName}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <Chip label={priceLabel} size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: T.gold, color: T.white }} />
              {dishes.length > 0 && (
                <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>
                  {dishes.length} dishes &middot; {dishes.filter((d) => d.chefPick).length} chef picks
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Category tabs */}
        {categories.length > 2 && (
          <Box sx={{ px: 2, pt: 1.5, bgcolor: T.white, borderBottom: `1px solid ${T.line}` }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 36,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: 12.5,
                  fontWeight: 600,
                  minHeight: 36,
                  py: 0,
                  color: T.slate,
                  "&.Mui-selected": { color: T.gold },
                },
                "& .MuiTabs-indicator": { bgcolor: T.gold, height: 2.5, borderRadius: "2px 2px 0 0" },
              }}
            >
              {categories.map((cat) => (
                <Tab key={cat} label={cat} value={cat} />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Dishes grid */}
        <Box sx={{ p: { xs: 2, sm: 2.5 }, overflow: "auto", maxHeight: "55vh" }}>
          {filteredDishes.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <RestaurantOutlinedIcon sx={{ fontSize: 40, color: T.slateLt, opacity: 0.4 }} />
              <Typography sx={{ fontSize: 13, color: T.slateLt, mt: 1 }}>No dishes available for preview</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
                gap: 2,
              }}
            >
              {filteredDishes.map((dish, i) => (
                <Box
                  key={`${dish.name}-${i}`}
                  onClick={() => openLightbox(i)}
                  sx={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    cursor: "pointer",
                    bgcolor: T.white,
                    border: `1px solid ${T.line}`,
                    boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.10)",
                      "& .dish-img": { transform: "scale(1.06)" },
                    },
                  }}
                >
                  <Box sx={{ position: "relative", aspectRatio: "1", overflow: "hidden" }}>
                    <Box
                      className="dish-img"
                      sx={{
                        width: "100%",
                        height: "100%",
                        backgroundImage: `url(${dish.image})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        transition: "transform 0.4s ease",
                      }}
                    />
                    {dish.chefPick && (
                      <Chip
                        icon={<StarRoundedIcon sx={{ fontSize: 11, color: `${T.gold} !important` }} />}
                        label="Chef Pick"
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          height: 22,
                          fontSize: 9.5,
                          fontWeight: 700,
                          bgcolor: "rgba(255,255,255,0.92)",
                          backdropFilter: "blur(6px)",
                          color: T.navy,
                          "& .MuiChip-icon": { ml: "3px" },
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        right: 8,
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: CATEGORY_COLORS[dish.category] || T.slate,
                        border: "2px solid white",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      }}
                    />
                  </Box>
                  <Box sx={{ px: 1.2, py: 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.navy, lineHeight: 1.2 }}>
                      {dish.name}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: CATEGORY_COLORS[dish.category] || T.slateLt, fontWeight: 500, mt: 0.2 }}>
                      {dish.category}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Dialog>

      {/* ── Premium Lightbox with slide + fade + scale ── */}
      {lightboxIdx !== null && filteredDishes[lightboxIdx] && (
        <Box
          onClick={closeLightbox}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            bgcolor: "rgba(0,0,0,0.88)",
            backdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            /* Open/close animations */
            animation: lbClosing
              ? `mgm-lbOut 280ms ${EASE} forwards`
              : `mgm-lbIn 300ms ${EASE}`,
            "@keyframes mgm-lbIn": {
              "0%": { opacity: 0 },
              "100%": { opacity: 1 },
            },
            "@keyframes mgm-lbOut": {
              "0%": { opacity: 1 },
              "100%": { opacity: 0 },
            },
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              bgcolor: "rgba(255,255,255,0.15)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
              zIndex: 5,
            }}
          >
            <CloseRoundedIcon sx={{ color: T.white }} />
          </IconButton>

          {/* Prev arrow */}
          <IconButton
            onClick={(e) => { e.stopPropagation(); prevLightbox(); }}
            sx={{
              position: "absolute",
              left: { xs: 8, sm: 24 },
              bgcolor: "rgba(255,255,255,0.15)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.25)", transform: "scale(1.08)" },
              transition: "all 0.2s ease",
              zIndex: 5,
              ...(bounceDir === "left" && {
                animation: `mgm-bounce-l 300ms ease`,
                "@keyframes mgm-bounce-l": {
                  "0%": { transform: "translateX(0)" },
                  "40%": { transform: "translateX(-6px)" },
                  "100%": { transform: "translateX(0)" },
                },
              }),
            }}
          >
            <ArrowBackIosNewRoundedIcon sx={{ color: T.white, fontSize: 20 }} />
          </IconButton>

          {/* Image + info container */}
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: 600,
              width: "90%",
              textAlign: "center",
              position: "relative",
              /* Zoom open/close animation for content */
              animation: lbClosing
                ? `mgm-contentOut 280ms ${EASE} forwards`
                : `mgm-contentIn 300ms ${EASE}`,
              "@keyframes mgm-contentIn": {
                "0%": { transform: "scale(0.9)", opacity: 0 },
                "100%": { transform: "scale(1)", opacity: 1 },
              },
              "@keyframes mgm-contentOut": {
                "0%": { transform: "scale(1)", opacity: 1 },
                "100%": { transform: "scale(0.92)", opacity: 0 },
              },
              /* Follow drag */
              ...(dragOffset ? {
                transform: `translateX(${dragOffset * 0.5}px)`,
                transition: "none",
              } : {}),
            }}
          >
            {/* Image container with crossfade */}
            <Box
              sx={{
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
                mb: 2,
                position: "relative",
                aspectRatio: "1",
              }}
            >
              {/* EXITING image — slides out with fade + scale + blur */}
              {isAnimating && prevLbIdx !== null && filteredDishes[prevLbIdx] && (
                <Box
                  component="img"
                  key={`exit-${prevLbIdx}`}
                  src={filteredDishes[prevLbIdx].image.replace("w=400&h=400", "w=800&h=800")}
                  alt=""
                  sx={{
                    position: "absolute", inset: 0, width: "100%", height: "100%",
                    objectFit: "cover", zIndex: 2,
                    willChange: "transform, opacity",
                    animation: `${exitAnimName} ${SLIDE_MS}ms ${EASE} forwards`,
                    [`@keyframes ${exitAnimName}`]: {
                      "0%": { transform: "translateX(0) scale(1)", opacity: 1, filter: "blur(0px)" },
                      "100%": { transform: `translateX(${exitX}) scale(0.94)`, opacity: 0, filter: "blur(2px)" },
                    },
                  }}
                />
              )}

              {/* ENTERING / CURRENT image — slides in with fade + scale */}
              <Box
                component="img"
                key={`img-${lightboxIdx}`}
                src={filteredDishes[lightboxIdx].image.replace("w=400&h=400", "w=800&h=800")}
                alt={filteredDishes[lightboxIdx].name}
                sx={{
                  width: "100%", display: "block",
                  position: "relative", zIndex: 1,
                  willChange: "transform, opacity",
                  ...(isAnimating ? {
                    animation: `${enterAnimName} ${SLIDE_MS}ms ${EASE} both`,
                    [`@keyframes ${enterAnimName}`]: {
                      "0%": { transform: `translateX(${enterX}) scale(1.02)`, opacity: 0 },
                      "100%": { transform: "translateX(0) scale(1)", opacity: 1 },
                    },
                  } : {}),
                }}
              />
            </Box>

            {/* Caption with fade animation */}
            <Box
              key={`cap-${lightboxIdx}`}
              sx={{
                animation: `mgm-capFade ${SLIDE_MS}ms ease`,
                "@keyframes mgm-capFade": {
                  "0%": { opacity: 0, transform: "translateY(6px)" },
                  "100%": { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: T.white }}>
                {filteredDishes[lightboxIdx].name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mt: 0.5 }}>
                <Chip
                  label={filteredDishes[lightboxIdx].category}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: 10,
                    fontWeight: 600,
                    bgcolor: CATEGORY_COLORS[filteredDishes[lightboxIdx].category] || T.slate,
                    color: T.white,
                  }}
                />
                {filteredDishes[lightboxIdx].chefPick && (
                  <Chip
                    icon={<StarRoundedIcon sx={{ fontSize: 11, color: `${T.gold} !important` }} />}
                    label="Chef Recommended"
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: 10,
                      fontWeight: 600,
                      bgcolor: "rgba(255,255,255,0.15)",
                      color: T.white,
                      "& .MuiChip-icon": { ml: "3px" },
                    }}
                  />
                )}
              </Box>
              {/* Counter */}
              <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.4)", mt: 1 }}>
                {lightboxIdx + 1} / {filteredDishes.length}
              </Typography>
            </Box>
          </Box>

          {/* Next arrow */}
          <IconButton
            onClick={(e) => { e.stopPropagation(); nextLightbox(); }}
            sx={{
              position: "absolute",
              right: { xs: 8, sm: 24 },
              bgcolor: "rgba(255,255,255,0.15)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.25)", transform: "scale(1.08)" },
              transition: "all 0.2s ease",
              zIndex: 5,
              ...(bounceDir === "right" && {
                animation: `mgm-bounce-r 300ms ease`,
                "@keyframes mgm-bounce-r": {
                  "0%": { transform: "translateX(0)" },
                  "40%": { transform: "translateX(6px)" },
                  "100%": { transform: "translateX(0)" },
                },
              }),
            }}
          >
            <ArrowForwardIosRoundedIcon sx={{ color: T.white, fontSize: 20 }} />
          </IconButton>
        </Box>
      )}
    </>
  );
};

export default MenuGalleryModal;
